import { useEffect, useState, useRef } from "react";
import axios from "axios";

import { uploadMediaFile } from "../../utills/fileUpload";
import { useLanguage } from "../../context/languageContext";
import { fetchAllGenresByLangOptions, normalizeCoverImage, convertToCloudFrontUrl } from "../../helper";


const useForm = (callback, validate, isModalVisible) => {
  const { languages, selectedLang, setSelectedLang, adminInfo } = useLanguage();

  const createInitialLangValues = (lang) => ({
    langCode: lang,
    title: "",
    tagline: "",
    author: [],
    publishedBy: "",
    summary: "",
    type: 'NonFiction',
    genre: [],
  });

  const createInitialTranslationsArr = () => languages.map((lang) => createInitialLangValues(lang));

  const [values, setValues] = useState({
    coverImage: {},
    publishedDate: "",
    numberOfPages: "",
    rating: null,
    link: "",
    translations: createInitialTranslationsArr()
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [editMediaError, setEditMediaError] = useState("");

  const [allGenresOptions, setAllgenresOptions] = useState([]);
  const [authorsData, setAuthorsData] = useState([]);

  // Author dropdown state
  const [isShowAuthor, setIsShowAuthor] = useState(false);
  const [searchAuthorName, setSearchAuthorName] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

  const dropdownRef = useRef(null);

  useEffect(() => {
    if (adminInfo && adminInfo.token && selectedLang) {
      fetchAllBooksAndGenressData();
    }
  }, [adminInfo?.token, selectedLang]);

  // Debounce search value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchAuthorName);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchAuthorName]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        handleDropdownClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Only fetch authors when there's a debounced search value and modal is visible
  useEffect(() => {
    if (isModalVisible && debouncedSearchValue && debouncedSearchValue.trim() !== '') {
      fetchAllAuthorsData();
    } else if (isModalVisible && (!debouncedSearchValue || debouncedSearchValue.trim() === '')) {
      // Clear authors data when search is empty
      setAuthorsData([]);
    }
  }, [isModalVisible, debouncedSearchValue]);

  const handleSearchAuthor = (authorName) => {
    setSearchAuthorName(authorName);
  };

  // Author dropdown handlers
  const handleInputChange = (value) => {
    setSearchAuthorName(value);
    setIsShowAuthor(true);
  };

  const handleAuthorSelect = (author, selectedLang) => {
    const currentAuthors = handleGetTranslationByLang(selectedLang).author || [];
    const isAlreadySelected = currentAuthors.some(a => a.authorId === author.value);

    if (!isAlreadySelected) {
      const newAuthors = [...currentAuthors, {
        authorName: author.label,
        authorId: author.value
      }];
      handleUpdateTranslationField("author", newAuthors, selectedLang);
    }

    setSearchAuthorName('');
    setIsShowAuthor(false);
  };

  const handleRemoveAuthor = (authorId, selectedLang) => {
    const currentAuthors = handleGetTranslationByLang(selectedLang).author || [];
    const updatedAuthors = currentAuthors.filter(a => a.authorId !== authorId);
    handleUpdateTranslationField("author", updatedAuthors, selectedLang);
  };

  const handleDropdownFocus = () => {
    setIsShowAuthor(true);
  };

  const handleDropdownClose = () => {
    setIsShowAuthor(false);
    setSearchAuthorName('');
  };

  const handleGetTranslationByLang = (langCode) => {
    return values.translations?.find((lang) => lang.langCode === langCode) || {};
  };

  const handleAddValues = (name, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleUpdateTranslationField = (fieldName, value, langCode) => {
    setValues((prevValues) => {
      const updatedTranslations = prevValues?.translations?.map((translation) =>
        translation.langCode === langCode
          ? {
            ...translation,
            [fieldName]: value,
            ...(fieldName === "type" && { genre: [] }),
          }
          : translation
      );
      return {
        ...prevValues,
        translations: updatedTranslations,
      };
    });
  };

  const handleSelectLanguage = (language) => {
    setSelectedLang(language);

    setValues((prevValues) => {
      const exists = prevValues?.translations?.some((t) => t.langCode === language);

      if (exists) {
        return { ...prevValues };
      }

      return {
        ...prevValues,
        translations: [...prevValues.translations, createInitialLangValues(language)],
      };
    });
  };

  const handleUploadMedia = async (event) => {
    // uploadType "cover" triggers backend variant generation (thumb/detail/master).
    const result = await uploadMediaFile(event, true, "cover");

    if (typeof result === "string") {
      setEditMediaError(result);
    } else if (typeof result === "object" && result.length > 0) {
      // Store the whole returned object with the S3 -> CloudFront swap applied to every URL.
      setValues((prevValues) => ({
        ...prevValues,
        coverImage: normalizeCoverImage(result[0]?.data),
      }));
    }
  };

  const fetchAllAuthorsData = async () => {
    setIsLoading(true);
    try {
      const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
      const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/favorite-author/search/${debouncedSearchValue}`, { headers });

      if (response.status) {
        const authorNamesArr = response?.data.map((author) => {
          const imageUrl = author.image ? normalizeCoverImage(author.image) : null;

          return {
            label: author.authorName,
            value: author.authorId,
            image: imageUrl?.uploadedImageUrl || imageUrl?.uploadedImageKey || null,
          };
        });

        setAuthorsData(authorNamesArr);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllBooksAndGenressData = async () => {
    const genresOptions = await fetchAllGenresByLangOptions(selectedLang, adminInfo.token);

    const options = genresOptions.map((genre) => ({
      label: genre.genreName,
      value: genre.genreId,
      type: genre.type,
    }));

    setAllgenresOptions(options);
  };

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmitting) {
      callback();
    }
  }, [errors, isSubmitting]);

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    setErrors(validate(values, selectedLang));
    setIsSubmitting(true);
  };


  return {
    values,
    setValues,
    isLoading,
    setIsSubmit,
    isSubmit,
    setErrors,
    errors,
    editMediaError,
    languages,
    authorsData,
    allGenresOptions,
    dropdownRef,
    createInitialTranslationsArr,

    isShowAuthor,
    setIsShowAuthor,
    searchAuthorName,
    handleSearchAuthor,
    handleInputChange,
    handleAuthorSelect,
    handleRemoveAuthor,
    handleDropdownFocus,
    handleDropdownClose,

    handleGetTranslationByLang,
    handleSelectLanguage,
    handleUpdateTranslationField,
    handleAddValues,
    handleUploadMedia,
    handleSubmit,
  }
};

export default useForm;
